import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

//Sets the months for the dropdown along with a value that is used to create the period
const months = [
    { value: 1, name: "January" }, { value: 2, name: "February" }, { value: 3, name: "March" },
    { value: 4, name: "April" }, { value: 5, name: "May" }, { value: 6, name: "June" },
    { value: 7, name: "July" }, { value: 8, name: "August" }, { value: 9, name: "September" },
    { value: 10, name: "October" }, { value: 11, name: "November" }, { value: 12, name: "December" }
];

//Returns the current year and the previous year for the year dropdown
const getYears = () => {
    const currentYear = new Date().getFullYear();
    return [currentYear, currentYear - 1];
};

//Sets the initial empty state for all stats
const initialStats = {
    TenantRetention: '',
    NationalTenants: '',
    ReversionRates: '',
    Vacancies: '',
    RetailVacancies: '',
    VacanciesFutureLet: '',
    FicaCompliance: ''
};

const TempTenancyStats = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();

    const [selectedMonth, setSelectedMonth] = useState(currentMonth);
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [stats, setStats] = useState(initialStats);
    const [isLoading, setIsLoading] = useState(false);

    const { apiFetch } = useAuth();

    const years = getYears();

    //function to disable future months if current year is selected
    const isMonthDisabled = (monthValue) => {
        if (selectedYear > currentYear) return false;
        if (selectedYear === currentYear && monthValue > currentMonth) return true;
        return false;
    };

    //Creates the period in YYYYMM format
    const getSelectedPeriod = () =>
        Number(`${selectedYear}${selectedMonth.toString().padStart(2, '0')}`);

    // Fetch stats for selected period
    useEffect(() => {
        const fetchStats = async () => {
            setIsLoading(true);
            try {
                const response = await apiFetch(`/api/Leasing/TempTenancyStats?period=${getSelectedPeriod()}`);
                const data = await response.json();
                if (data.success && data.data) {
                    setStats({
                        TenantRetention: data.data.tenantRetention ?? 0,
                        NationalTenants: data.data.nationalTenants ?? 0,
                        ReversionRates: data.data.reversionRates ?? 0,
                        Vacancies: data.data.vacancies ?? 0,
                        RetailVacancies: data.data.retailVacancies ?? 0,
                        VacanciesFutureLet: data.data.vacanciesFutureLet ?? 0,
                        FicaCompliance: data.data.ficaCompliance ?? 0
                    });
                } else {
                    setStats(initialStats);
                }
            } catch (catchError) {
                Sentry.captureException(catchError);
                setStats(initialStats);
            } finally {
                setIsLoading(false);
            }
        };
        fetchStats();
        // eslint-disable-next-line
    }, [selectedMonth, selectedYear]);

    //Handles input changes and only allows numbers and decimals
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Only allow numbers and decimals
        if (value === '' || /^\d+(\.\d{0,4})?$/.test(value)) {
            setStats(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    //Handles the save button click, sends the data to the API
    const handleSave = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const payload = {
                period: getSelectedPeriod(),
                tenantRetention: stats.TenantRetention === '' ? null : parseFloat(stats.TenantRetention),
                nationalTenants: stats.NationalTenants === '' ? null : parseFloat(stats.NationalTenants),
                reversionRates: stats.ReversionRates === '' ? null : parseFloat(stats.ReversionRates),
                vacancies: stats.Vacancies === '' ? null : parseFloat(stats.Vacancies),
                retailVacancies: stats.RetailVacancies === '' ? null : parseFloat(stats.RetailVacancies),
                vacanciesFutureLet: stats.VacanciesFutureLet === '' ? null : parseFloat(stats.VacanciesFutureLet),
                ficaCompliance: stats.FicaCompliance === '' ? null : parseFloat(stats.FicaCompliance)
            };
            const response = await apiFetch('/api/Leasing/TempTenancyStats', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Data saved successfully!');
            } else {
                toast.error('Failed to save data.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="temp-tenancy-stats-container" style={{paddingBottom: "15px"}}>
            <h1>Temp Tenancy Stats</h1>
            <div className="period-selector">
                <label>Month:</label>
                <select value={selectedMonth} onChange={e => setSelectedMonth(Number(e.target.value))}>
                    {months.map(month => (
                        <option key={month.value} value={month.value} disabled={isMonthDisabled(month.value)}>
                            {month.name}
                        </option>
                    ))}
                </select>
                <label>Year:</label>
                <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}>
                    {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                    ))}
                </select>
            </div>
            <form onSubmit={handleSave}>
                <div style={{ paddingBottom: '10px' }}>
                    <label>Tenant Retention</label>
                    <input
                        type="text"
                        name="TenantRetention"
                        value={stats.TenantRetention}
                        onChange={handleInputChange}
                        inputMode="decimal"
                        placeholder="Tenant Retention"
                    />
                </div>
                <div style={{ paddingBottom: '10px' }}>
                    <label>National Tenants</label>
                    <input
                        type="text"
                        name="NationalTenants"
                        value={stats.NationalTenants}
                        onChange={handleInputChange}
                        inputMode="decimal"
                        placeholder="National Tenants"
                    />
                </div>
                <div style={{ paddingBottom: '10px' }}>
                    <label>Reversion Rates</label>
                    <input
                        type="text"
                        name="ReversionRates"
                        value={stats.ReversionRates}
                        onChange={handleInputChange}
                        inputMode="decimal"
                        placeholder="Reversion Rates"
                    />
                </div>
                <div style={{ paddingBottom: '10px' }}>
                    <label>Vacancies</label>
                    <input
                        type="text"
                        name="Vacancies"
                        value={stats.Vacancies}
                        onChange={handleInputChange}
                        inputMode="decimal"
                        placeholder="Vacancies"
                    />
                </div>
                <div style={{ paddingBottom: '10px' }}>
                    <label>Retail Vacancies</label>
                    <input
                        type="text"
                        name="RetailVacancies"
                        value={stats.RetailVacancies}
                        onChange={handleInputChange}
                        inputMode="decimal"
                        placeholder="Retail Vacancies"
                    />
                </div>
                <div style={{ paddingBottom: '10px' }}>
                    <label>Vacancies Future Let</label>
                    <input
                        type="text"
                        name="VacanciesFutureLet"
                        value={stats.VacanciesFutureLet}
                        onChange={handleInputChange}
                        inputMode="decimal"
                        placeholder="Vacancies Future Let"
                    />
                </div>
                <div style={{ paddingBottom: '10px' }}>
                    <label>FICA Compliance</label>
                    <input
                        type="text"
                        name="FicaCompliance"
                        value={stats.FicaCompliance}
                        onChange={handleInputChange}
                        inputMode="decimal"
                        placeholder="FICA Compliance"
                    />
                </div>
                <button type="submit" disabled={isLoading}>
                    {isLoading ? 'Saving...' : 'Save'}
                </button>
            </form>
</div>
    );
};

export default TempTenancyStats;